import type { ReactElement } from "react";
import { useState } from "react";

import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { FiltersChipRemovedTooltip } from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersChipRemovedTooltip/FiltersChipRemovedTooltip";
import { StyledSDKValueDivider } from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppSdkFilter/styles";
import type FindTechnologyFilter from "app/pages/sales-intelligence/modules/leads-search-filters/models/TechnologyFilter/TechnologyFilter";
import InclusionTechnologiesChip from "app/pages/sales-intelligence/modules/technology/components/InclusionTechnologiesChip/InclusionTechnologiesChip";
import { TechnologyModal } from "app/pages/sales-intelligence/modules/technology/components/modals/TechnologyModal/TechnologyModal";
import type { TechnologiesFilterDto } from "app/pages/sales-intelligence/modules/technology/types/technologies";
import { hasSameTechnologyObject } from "app/pages/sales-intelligence/modules/technology/utils/technologies";
import { operatorOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

export const TechnologyFilterResult = ({
    filter: { options },
    value,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindTechnologyFilter>): ReactElement | null => {
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<TechnologiesFilterDto | null>(null);

    const handleClose = () => {
        setIsOpenModal(false);
    };

    const handleApply = (item: TechnologiesFilterDto, selectedItem?: TechnologiesFilterDto) => {
        const chipValues = (value || []).map((chipItem: TechnologiesFilterDto) => {
            if (selectedItem && hasSameTechnologyObject(chipItem, selectedItem)) {
                return item;
            }

            return chipItem;
        });
        onFilterUpdate(chipValues);
        setIsOpenModal(false);
    };

    const handleOnDelete = (selectedItem: TechnologiesFilterDto) => {
        const filteredValue = value?.filter(
            (item: TechnologiesFilterDto) => !hasSameTechnologyObject(item, selectedItem),
        );

        const newValue = filteredValue && filteredValue.length > 0 ? filteredValue : undefined;

        onFilterValueRemove?.(newValue);
        onFilterUpdate(newValue);
    };

    const handleOnEdit = (selectedItem: TechnologiesFilterDto) => {
        setSelectedItem(selectedItem);
        setIsOpenModal(true);
    };

    if (!value?.length) {
        return null;
    }

    return (
        <div>
            <TechnologyModal
                isOpen={isOpenModal}
                selected={selectedItem}
                onClose={handleClose}
                onCancel={handleClose}
                onApply={handleApply}
                options={options}
                value={value}
            />

            {value && value?.length > 0
                ? value.map((item: TechnologiesFilterDto, i) => {
                      const isChipRemoved = onGetLifecycleStatus?.(item) === "removed";

                      return (
                          <>
                              {i > 0 ? <StyledSDKValueDivider>{operatorOptionMap.and}</StyledSDKValueDivider> : null}

                              <FiltersChipRemovedTooltip isEnabled={isChipRemoved}>
                                  <InclusionTechnologiesChip
                                      item={item}
                                      hideActionButton={!isActionEnabled || isChipRemoved}
                                      onEdit={handleOnEdit}
                                      onDelete={handleOnDelete}
                                  />
                              </FiltersChipRemovedTooltip>
                          </>
                      );
                  })
                : null}
        </div>
    );
};
